import { Loader2 } from 'lucide-react'
import { useMemo, useState } from 'react'
import { LABEL_COLORS } from '@/components/neo4j/cytoscape/entity-icons'
import type { NeighborEdge, NeighborNode } from '@/components/neo4j/shared/entity-details/types'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { graphApi } from '@/services/api/graph'
import { cn } from '@/utils/cn'

interface GlobalExpandConfirmProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  caseId: string
  /** Per-node unloaded-neighbour counts (the same data feeding the badges). */
  counts: Record<string, Record<string, number>>
  /** Called after a successful batch expand with everything to merge. */
  onMerge: (nodes: NeighborNode[], edges: NeighborEdge[]) => Promise<void> | void
}

/**
 * Confirmation modal for the toolbar "Expand all" action. Sums the
 * per-node neighbour counts into a global preview so the user sees what
 * they're about to add, then batch-fetches `getNodeNeighbors` for every
 * node and hands the union off to the caller's `mergeIntoView`.
 */
export function GlobalExpandConfirm({
  open,
  onOpenChange,
  caseId,
  counts,
  onMerge,
}: GlobalExpandConfirmProps) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { total, byLabel, nodeIds } = useMemo(() => {
    const acc: Record<string, number> = {}
    const ids: string[] = []
    let t = 0
    for (const [nodeId, byL] of Object.entries(counts)) {
      let nodeTotal = 0
      for (const [label, c] of Object.entries(byL)) {
        acc[label] = (acc[label] ?? 0) + c
        nodeTotal += c
      }
      if (nodeTotal > 0) {
        ids.push(nodeId)
        t += nodeTotal
      }
    }
    return { total: t, byLabel: acc, nodeIds: ids }
  }, [counts])

  async function confirm() {
    setBusy(true)
    setError(null)
    try {
      const responses = await Promise.all(nodeIds.map(id => graphApi.getNodeNeighbors(caseId, id)))
      const seenNodes = new Set<string>()
      const seenEdges = new Set<string>()
      const mergedNodes: NeighborNode[] = []
      const mergedEdges: NeighborEdge[] = []
      for (const res of responses) {
        for (const n of (res.nodes ?? []) as unknown as NeighborNode[]) {
          if (seenNodes.has(n.id)) continue
          seenNodes.add(n.id)
          mergedNodes.push(n)
        }
        for (const e of (res.relationships ?? []) as unknown as NeighborEdge[]) {
          if (seenEdges.has(e.id)) continue
          seenEdges.add(e.id)
          mergedEdges.push(e)
        }
      }
      await onMerge(mergedNodes, mergedEdges)
      onOpenChange(false)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const labelEntries = Object.entries(byLabel).sort((a, b) => b[1] - a[1])

  return (
    <Dialog open={open} onOpenChange={busy ? undefined : onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Expand all neighbours?</DialogTitle>
          <DialogDescription>
            Pulls 1-hop neighbours for every node in the current view that has unloaded connections.
            The Object Set grows; nothing is removed.
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-lg border border-border bg-muted/40 p-3">
          <div className="text-2xl font-semibold text-foreground">{total}</div>
          <div className="text-2xs uppercase tracking-[0.14em] text-muted-foreground">
            entities to add
          </div>
        </div>
        <div className="max-h-48 overflow-y-auto rounded-md border border-border/60">
          {labelEntries.length === 0 ? (
            <div className="px-3 py-4 text-center text-xs text-muted-foreground">
              No unloaded neighbours in this view.
            </div>
          ) : (
            labelEntries.map(([label, count]) => {
              const color = LABEL_COLORS[label] ?? { fill: '#64748b', stroke: '#475569' }
              return (
                <div
                  key={label}
                  className={cn(
                    'flex items-center justify-between gap-3 border-b border-border/40 px-3 py-2 last:border-b-0',
                    'text-xs'
                  )}
                >
                  <div className="flex min-w-0 items-center gap-2.5">
                    <span
                      className="size-2.5 shrink-0 rounded-full border"
                      style={{ backgroundColor: color.fill, borderColor: color.stroke }}
                    />
                    <span className="truncate text-foreground">{label}</span>
                  </div>
                  <span className="font-mono tabular-nums text-muted-foreground">{count}</span>
                </div>
              )
            })
          )}
        </div>
        {error && (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-2xs text-destructive">
            {error}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" disabled={busy} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={busy || total === 0} onClick={() => void confirm()}>
            {busy ? <Loader2 className="mr-2 size-3.5 animate-spin" /> : null}
            Add all {total > 0 ? total : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
